import { DesignStandard, roofShapes } from '@paragon/contract-library';

import {
  exposureCategories,
  terrainCategories,
  thermalConditions,
} from '../../components/interface/subpanels/settings/loading/components/loadSettingsEnumOptions';
import { ValidDesignCodes } from '../../components/interface/subpanels/settings/loading/validDesignCodes';

/**
 * The values each settings choice accepts, spelled as the app stores them, by the setting's path in the settings. The
 * server refuses anything else, so the Assistant is shown these instead of guessing "C_ScatteredObstructions".
 */

// IBC and IRC 2009 refer to ASCE 7-05, which the app doesn't support, so the settings panel doesn't offer them.
const designCodes = ValidDesignCodes.filter(
  ([buildingCode]) => buildingCode !== 'Ibc2009' && buildingCode !== 'Irc2009',
);

export const SETTING_CHOICES = {
  load: {
    buildingCode: designCodes.map(([buildingCode]) => buildingCode),
    terrainExposureCategory: terrainCategories,
    riskCategory: ['I', 'II', 'III', 'IV'],
    enclosureClassification: ['Enclosed', 'PartiallyEnclosed', 'PartiallyOpen', 'Open'],
    windDesignMethod: [
      'WindAsceCc',
      'WindAsceMwfrsEnvelope',
      'WindAsceMwfrsDirectional',
      'WindAsceHybridEnvelope',
      'WindAsceHybridDirectional',
    ],
    roofShape: roofShapes,
    activeSnowLoad: ['UltimateGround', 'AsdGround', 'Roof'],
    exposure: exposureCategories,
    thermalCondition: thermalConditions,
    dragLoadUnit: ['Pounds', 'Plf'],
    occupancyUse: ['None', 'Construction', 'Storage', 'Sleeping', 'Living'],
    otherUse: ['None', 'Construction', 'Storage', 'Sleeping', 'Living'],
  },
  analysis: {
    designStandard: Object.values(DesignStandard).filter((standard) => standard !== DesignStandard.Unknown),
    temperature: ['LessThan100', 'GreaterThan100', 'GreaterThan125'],
    multiPlyConnectionType: ['Nailed', 'Bolted', 'Screwed'],
  },
} satisfies Record<'load' | 'analysis', Record<string, readonly string[]>>;

/** What the choices alone don't say: which values go together. */
export const SETTING_CHOICE_NOTES = {
  load: [
    'buildingCode goes with the analysis settings’ designStandard: ' +
      designCodes.map(([buildingCode, designStandard]) => `${buildingCode} with ${designStandard}`).join(', ') +
      '. Change both.',
    'enclosureClassification PartiallyOpen needs a code on ASCE 7-16 or later, and Open can’t be used with the ' +
      'WindAsceMwfrsEnvelope or WindAsceHybridEnvelope windDesignMethod.',
    'meanRoofHeight, buildingWidth, and buildingLength are in feet; loads are in psf and windSpeed in mph.',
  ],
  analysis: ['designStandard goes with the load settings’ buildingCode; see the load settings’ notes.'],
};

/** The choices that apply to these settings, leaving out any the settings don't have. */
export function getSettingChoices(kind: 'load' | 'analysis', settings: object) {
  return Object.fromEntries(Object.entries(SETTING_CHOICES[kind]).filter(([path]) => path in settings));
}
